import {
  Alert,
  SelectedSources,
  isExplicitUnknownSelectedForFieldPath,
  schAloftArea,
  schAloftPrice,
  schPairedCostRouteItem,
  schSiteRating,
  schSixTierQuality,
  syntaxFNMA,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import {
  CompPropertyDataState,
  PropertyDataState,
  SubjectPropertyDataState,
} from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';

const schParkingSpaces = z
  .object({
    spaces: z.number().int().nonnegative().optional(),
    hasFeature: z.boolean(),
  })
  .optional()
  .superRefine((data, ctx) => {
    if (data?.hasFeature && data?.spaces === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['spaces'],
      });
    }
  });

const schSalesComparablesImprovementFields = z.object({
  gla: schAloftArea,
  bedrooms: z.number().int().nonnegative(),
  fullBaths: z.number().int().nonnegative(),
  halfBaths: z.number().int().nonnegative(),
  age: z.number().int().nonnegative(),
  uadQuality: schSixTierQuality,
  uadCondition: schSixTierQuality,
  garage: schParkingSpaces,
  detachedGarage: schParkingSpaces,
  carport: schParkingSpaces,
});

const schSalesComparablesFields = z.object({
  id: z.string(),
  transaction: z
    .object({
      status: z.string().optional(),
      price: schAloftPrice.optional(),
      saleDate: syntaxFNMA.schIsoMonth.optional(),
      priceUnknown: z.boolean(),
      saleDateUnknown: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.price === undefined && !data.priceUnknown) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['price'],
        });
      }
      const saleDateRequired = !data.status || data.status === 'SettledSale';
      if (saleDateRequired && data.saleDate === undefined && !data.saleDateUnknown) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['saleDate'],
        });
      }
    }),
  site: z.object({
    area: schAloftArea,
    influence: z.string(),
    influenceRating: schSiteRating,
    view: z.string(),
    viewRating: schSiteRating,
  }),
  improvements: schSalesComparablesImprovementFields.extend({
    arv: schSalesComparablesImprovementFields.optional(),
  }),
});

const GARAGE_FIELD_PRIORITY = ['garage', 'detachedGarage', 'carport'] as const;

const issueToCellKey = (id: string, issue: z.ZodIssue): PropertyGridCellKey | undefined => {
  const path = issue.path as (string | number)[];
  if (!path.length) return undefined;
  return `${id}-editableData.${path.join('.')}` as PropertyGridCellKey;
};

const getTargetCellKeys = (id: string, issues: z.ZodIssue[]): PropertyGridCellKey[] => [
  ...new Set(
    issues
      .map((issue) => issueToCellKey(id, issue))
      .filter((key): key is PropertyGridCellKey => key !== undefined),
  ),
];

const getFocusCellKey = (id: string, issues: z.ZodIssue[]): PropertyGridCellKey | undefined => {
  const garageField = GARAGE_FIELD_PRIORITY.find((field) =>
    issues.some(
      (issue) =>
        issue.path[0] === 'improvements' && issue.path[1] === field && issue.path[2] === 'spaces',
    ),
  );
  if (garageField) {
    return `${id}-editableData.improvements.${garageField}.spaces` as PropertyGridCellKey;
  }
  return getTargetCellKeys(id, issues)[0];
};

const getSelectedSources = (propertyDataEntity: PropertyDataState): SelectedSources | undefined =>
  propertyDataEntity.id === 'subject'
    ? (propertyDataEntity as SubjectPropertyDataState).subjectData.selectedSources
    : (propertyDataEntity as CompPropertyDataState).compData.selectedSources;

const mapTransactionFields = (
  editableData: { transaction?: { status?: string; price?: unknown; saleDate?: string } },
  selectedSources: SelectedSources | undefined,
) => ({
  status: editableData.transaction?.status,
  price: editableData.transaction?.price,
  saleDate: editableData.transaction?.saleDate,
  priceUnknown: isExplicitUnknownSelectedForFieldPath(
    selectedSources,
    'editableData.transaction.price',
  ),
  saleDateUnknown: isExplicitUnknownSelectedForFieldPath(
    selectedSources,
    'editableData.transaction.saleDate',
  ),
});

const mapPropertyDataEntityToSalesComparableFieldsSchema = (
  propertyDataEntity: PropertyDataState,
) => {
  const selectedSources = getSelectedSources(propertyDataEntity);

  if (propertyDataEntity.id === 'subject') {
    const { editableData } = (propertyDataEntity as SubjectPropertyDataState).subjectData;
    const arvImprovements = editableData.arv
      ? {
          gla: editableData.arv?.improvements?.gla,
          bedrooms: editableData.arv?.improvements?.bedrooms,
          fullBaths: editableData.arv?.improvements?.fullBaths,
          halfBaths: editableData.arv?.improvements?.halfBaths,
          age: editableData.improvements?.age,
          uadQuality: editableData.arv?.improvements?.uadQuality,
          uadCondition: editableData.arv?.improvements?.uadCondition,
          garage: editableData.arv?.improvements?.garage,
          detachedGarage: editableData.arv?.improvements?.detachedGarage,
          carport: editableData.arv?.improvements?.carport,
        }
      : undefined;
    return schSalesComparablesFields.omit({ transaction: true }).safeParse({
      id: propertyDataEntity.id,
      improvements: {
        gla: editableData.improvements?.gla,
        bedrooms: editableData.improvements?.bedrooms,
        fullBaths: editableData.improvements?.fullBaths,
        halfBaths: editableData.improvements?.halfBaths,
        age: editableData.improvements?.age,
        uadQuality: editableData.improvements?.uadQuality,
        uadCondition: editableData.improvements?.uadCondition,
        garage: editableData.improvements?.garage,
        detachedGarage: editableData.improvements?.detachedGarage,
        carport: editableData.improvements?.carport,
        arv: arvImprovements,
      },
      site: {
        area: editableData.site?.area,
        influence: editableData.site?.influence,
        influenceRating: editableData.site?.influenceRating,
        view: editableData.site?.view,
        viewRating: editableData.site?.viewRating,
      },
    });
  }
  const { editableData } = (propertyDataEntity as CompPropertyDataState).compData;

  return schSalesComparablesFields.safeParse({
    id: propertyDataEntity.id,
    transaction: mapTransactionFields(editableData, selectedSources),
    improvements: {
      gla: editableData.improvements?.gla,
      bedrooms: editableData.improvements?.bedrooms,
      fullBaths: editableData.improvements?.fullBaths,
      halfBaths: editableData.improvements?.halfBaths,
      age: editableData.improvements?.age,
      uadQuality: editableData.improvements?.uadQuality,
      uadCondition: editableData.improvements?.uadCondition,
      garage: editableData.improvements?.garage,
      detachedGarage: editableData.improvements?.detachedGarage,
      carport: editableData.improvements?.carport,
    },
    site: {
      area: editableData.site?.area,
      influence: editableData.site?.influence,
      influenceRating: editableData.site?.influenceRating,
      view: editableData.site?.view,
      viewRating: editableData.site?.viewRating,
    },
  });
};

export const missingPropertyData: AlertDefinition = ({
  propertyDataEntities,
  compSequence,
  propertyType,
  compType,
  fullCompSequence,
  order,
}) => {
  const triggers = getAlertTriggers({
    fixRoute: schPairedCostRouteItem.enum.features,
    order,
  });

  const errors = ['subject', ...compSequence]
    .map((id) => {
      if (propertyType && id === 'subject' && propertyType !== 'subject') return;
      if (propertyType && id !== 'subject' && propertyType !== 'comp') return;

      const propertyDataEntity = propertyDataEntities[id];
      if (!propertyDataEntity) return;

      const result = mapPropertyDataEntityToSalesComparableFieldsSchema(propertyDataEntity);
      if (result.success) return;

      const propertyName =
        id === 'subject' ? 'Subject' : `Comp ${(fullCompSequence?.indexOf(id) ?? 0) + 1}`;

      const targetCellKey = getFocusCellKey(id, result.error.issues);
      const targetCellKeys = getTargetCellKeys(id, result.error.issues);

      return {
        type: 'missingPropertyData',
        status: 'active',
        severity: 'error',
        orderType: ['appraisify'],
        message: `${propertyName} has missing data.`,
        propertyID: id,
        ...(compType && { compType }),
        fixingInfo: {
          tips: 'One or more of the rows for the property is incomplete.',
          pairedCostRouteItem: 'features',
          fixingMethod: ['action'],
          commentAlwaysRequired: false,
          targetCellKey,
          targetCellKeys,
        },
        triggers,
      } as Alert;
    })
    .filter((alert): alert is Alert => alert !== undefined);

  return errors;
};
